'use client';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Avatar, Box, Chip, Container, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { SafeDate } from '@/components/Utility/SafeDate';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { ResourceArticle, UploadFile } from '@/types';

interface ArticleHeroProps {
  title: string;
  heroImage: UploadFile;
  resourceArticle: ResourceArticle;
}

export function ArticleHero({ title, heroImage, resourceArticle }: ArticleHeroProps) {
  const theme = useTheme();
  const [isClient, setIsClient] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => setIsClient(true), []);

  const author = resourceArticle?.Author ?? undefined;
  const category = resourceArticle?.ResourceCategory ?? undefined;

  const publishDate = resourceArticle?.ShowDatePublished
    ? (resourceArticle?.OverridePublishDate ?? resourceArticle?.publishedAt)
    : undefined;

  const heroImageUrl = useMemo(
    () => getImageUrl({ image: heroImage, size: 'large' }) || '/assets/hero_image.png',
    [heroImage]
  );

  const mediaElement = useMemo(() => {
    if (!heroImageUrl) return null;
    const isVideo =
      heroImage?.mime?.startsWith('video/') ||
      heroImageUrl.endsWith('.mp4') ||
      heroImageUrl.endsWith('.mov');

    const mediaStyles = {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      display: 'block',
    };

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '4 / 3',
          overflow: 'hidden',
        }}
      >
        {isVideo && isClient && !videoError ? (
          <video
            src={heroImageUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onError={() => setVideoError(true)}
            style={mediaStyles}
          />
        ) : (
          <Image
            src={heroImageUrl}
            alt={heroImage?.alternativeText || 'Hero media'}
            fill
            priority
            fetchPriority="high"
            sizes="(max-width: 900px) 100vw, 40vw"
            style={mediaStyles}
          />
        )}
      </Box>
    );
  }, [heroImage, heroImageUrl, isClient, videoError]);

  return (
    <Box
      data-component="ArticleHero"
      sx={{
        background: `radial-gradient(circle at 50% 40%, ${theme.palette.brand.magentaGlow} 0%, ${alpha(theme.palette.purple[500], 0)} 35%), ${theme.palette.purple[800]}`,
      }}
    >
      <Container
        maxWidth="xxl"
        sx={{
          py: { xs: 4, md: 6 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          alignItems: 'center',
          columnGap: { xs: 4, md: 8 },
          rowGap: 4,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {category?.CategoryName && (
            <Chip
              component={Link}
              href={`/resources/${category.Slug}`}
              label={category.CategoryName}
              clickable
              sx={{
                alignSelf: 'flex-start',
                bgcolor: alpha(theme.palette.brand.magentaGlow, 0.3),
                color: theme.palette.common.white,
                fontWeight: 600,
                fontSize: '0.8125rem',
                letterSpacing: '0.01em',
                borderRadius: 999,
                height: 30,
                px: 1.5,
                border: `1px solid ${alpha(theme.palette.brand.pinkHalo, 0.4)}`,
                backdropFilter: 'blur(6px)',
                boxShadow: `0 0 12px ${alpha(theme.palette.brand.magentaGlow, 0.4)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.brand.magentaGlow, 0.5),
                  borderColor: alpha(theme.palette.brand.pinkHalo, 0.7),
                  boxShadow: `0 0 18px ${alpha(theme.palette.brand.magentaGlow, 0.6)}`,
                },
              }}
            />
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 400,
                color: theme.palette.common.white,
                lineHeight: 1.2,
                fontSize: { xs: '2.5rem', md: '4rem' },
              }}
            >
              {title}
            </Typography>

            {resourceArticle?.ResourceIntro && (
              <Box
                sx={{
                  color: theme.palette.common.white,
                  fontSize: '1rem',
                  lineHeight: 1.6,
                }}
              >
                <GlobalBlockRenderer content={resourceArticle.ResourceIntro} />
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 3,
                rowGap: 1.5,
                color: theme.palette.common.white,
              }}
            >
              {author && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    src={getImageUrl({ image: author?.Avatar, size: 'small' }) ?? undefined}
                    alt={author?.Name ?? ''}
                    sx={{ width: 48, height: 48 }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.125rem',
                        lineHeight: '26px',
                        textDecoration: 'underline',
                        textUnderlineOffset: 3,
                      }}
                    >
                      {author.Name}
                    </Typography>
                    {author?.Title && (
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          color: theme.palette.common.white,
                        }}
                      >
                        {author.Title}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {publishDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: 1.6 }}>
                    <SafeDate date={publishDate} formatString="MMMM D, YYYY" />
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {mediaElement}
      </Container>
    </Box>
  );
}
